import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Stack,
  Slider,
  TextField,
} from "@mui/material";
import {
  VideoLibrary,
  Check,
  Link as LinkIcon,
  Add,
  Delete,
  RadioButtonChecked,
  RadioButtonUnchecked,
  VpnKey,
  ContentCopy,
  Autorenew,
  People as PeopleIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import UserManagement from "./UserManagement";
import { videoService } from "../services/api";
import { API_URL } from "../config/constants";

// Constantes de Vimeo
const VIMEO_CLIENT_ID = "0c005f069406a702b6c5c4490136399086cf16a6";
// Construir redirect_uri desde la URL base de la API (evita dobles slashes y mantiene consistencia dev/prod)
const VIMEO_REDIRECT_URI = new URL("/api/vimeo/callback", API_URL).toString();
// Scope completo con upload aprobado por Vimeo
const VIMEO_SCOPES = "public private create edit delete upload";

const Settings = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vimeoAuthInProgress, setVimeoAuthInProgress] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    autoUpload: false,
    privacy: "unlisted",
    hasToken: false,
    userInfo: null,
    selectedTeam: null,
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState({
    hasKey: false,
    maskedKey: null,
    lastGeneratedAt: null,
  });
  const [apiKeyVisible, setApiKeyVisible] = useState("");
  const [apiKeyLoading, setApiKeyLoading] = useState(false);

  // Estado para configuración general de la app
  const [appSettings, setAppSettings] = useState({ maxFileSizeGB: 2.5 });
  const [appSettingsLoading, setAppSettingsLoading] = useState(false);
  const [pendingMaxFileSize, setPendingMaxFileSize] = useState(null);

  // Cargar configuración al montar
  useEffect(() => {
    loadConfig();
    loadApiKeyStatus();
    loadAppSettings();
  }, []);

  // Cargar cuentas conectadas cuando hay token
  useEffect(() => {
    if (config.hasToken && config.enabled) {
      loadAccounts();
    }
  }, [config.hasToken, config.enabled]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await videoService.getVimeoConfig();
      if (response.success) {
        setConfig(response.data);
      }
    } catch (err) {
      setError(err.message || "Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const response = await videoService.getVimeoAccounts();
      if (response.success) {
        setAccounts(response.data || []);
      }
    } catch (err) {
      console.error("Error cargando cuentas:", err);
      setAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadAppSettings = async () => {
    try {
      const response = await videoService.getAppSettings();
      if (response.success) {
        setAppSettings(response.data);
        setPendingMaxFileSize(response.data.maxFileSizeGB);
      }
    } catch (err) {
      console.error('Error cargando configuración general:', err);
    }
  };

  const handleSaveMaxFileSize = async () => {
    if (pendingMaxFileSize === appSettings.maxFileSizeGB) return;

    try {
      setAppSettingsLoading(true);
      setError('');
      setSuccess('');

      const response = await videoService.updateAppSettings({
        maxFileSizeGB: pendingMaxFileSize,
      });

      if (response.success) {
        setAppSettings(response.data);
        setSuccess(`Tamaño máximo actualizado a ${pendingMaxFileSize} GB`);
      }
    } catch (err) {
      setError(err.message || 'Error actualizando tamaño máximo');
    } finally {
      setAppSettingsLoading(false);
    }
  };

  const loadApiKeyStatus = async () => {
    try {
      const response = await videoService.getApiKeyStatus();
      if (response.success) {
        setApiKeyStatus(response.data);
      }
    } catch (err) {
      console.error("Error cargando estado API Key:", err);
    }
  };

  const handleGenerateApiKey = async () => {
    if (apiKeyStatus.hasKey) {
      const confirmed = window.confirm(
        "Esto regenerará la API Key actual y dejará de funcionar la anterior. ¿Continuar?"
      );
      if (!confirmed) return;
    }

    try {
      setApiKeyLoading(true);
      setError("");
      setSuccess("");

      const response = await videoService.generateApiKey();
      if (response.success) {
        setApiKeyVisible(response.data.apiKey);
        setApiKeyStatus({
          hasKey: true,
          maskedKey: response.data.maskedKey,
          lastGeneratedAt: response.data.lastGeneratedAt,
        });
        setSuccess(
          "API Key generada. Guárdala ahora: por seguridad luego solo verás la versión enmascarada."
        );
      }
    } catch (err) {
      setError(err.message || "Error generando API Key");
    } finally {
      setApiKeyLoading(false);
    }
  };

  const handleCopyApiKey = async () => {
    if (!apiKeyVisible) return;
    try {
      await navigator.clipboard.writeText(apiKeyVisible);
      setSuccess("API Key copiada al portapapeles");
    } catch (err) {
      setError("No se pudo copiar la API Key");
    }
  };

  const handleToggleEnabled = async (event) => {
    const newEnabled = event.target.checked;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await videoService.updateVimeoConfig({
        enabled: newEnabled,
      });

      if (response.success) {
        setConfig(response.data);
        setSuccess(
          newEnabled
            ? "Vimeo activado correctamente"
            : "Vimeo desactivado correctamente"
        );
      }
    } catch (err) {
      setError(err.message || "Error actualizando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAutoUpload = async (event) => {
    const newAutoUpload = event.target.checked;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await videoService.updateVimeoConfig({
        autoUpload: newAutoUpload,
      });

      if (response.success) {
        setConfig(response.data);
        setSuccess(
          newAutoUpload
            ? "Subida automática activada"
            : "Subida manual activada"
        );
      }
    } catch (err) {
      setError(err.message || "Error actualizando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handlePrivacyChange = async (event) => {
    const newPrivacy = event.target.value;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await videoService.updateVimeoConfig({
        privacy: newPrivacy,
      });

      if (response.success) {
        setConfig(response.data);
        setSuccess("Privacidad actualizada correctamente");
      }
    } catch (err) {
      setError(err.message || "Error actualizando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleAccountSelect = async (accountId) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await videoService.selectVimeoAccount(accountId);

      if (response.success) {
        const selectedAccount = accounts.find(a => a.id === accountId);
        setSuccess(`Cuenta "${selectedAccount?.name || 'principal'}" seleccionada`);
        await loadAccounts(); // Recargar para actualizar el estado de selección
      }
    } catch (err) {
      setError(err.message || "Error seleccionando cuenta");
    } finally {
      setSaving(false);
    }
  };

  const handleAddAccount = () => {
    // Abrir popup para conectar otra cuenta de Vimeo
    if (addingAccount) return;

    setError("");
    setSuccess("");
    setAddingAccount(true);

    const state = Math.random().toString(36).substring(7) + "_additional";
    const params = new URLSearchParams({
      response_type: "code",
      client_id: VIMEO_CLIENT_ID,
      redirect_uri: VIMEO_REDIRECT_URI,
      scope: VIMEO_SCOPES,
      state: state,
    });

    sessionStorage.setItem("vimeo_oauth_state", state);
    sessionStorage.setItem("vimeo_redirect_uri", VIMEO_REDIRECT_URI);
    sessionStorage.setItem("vimeo_adding_account", "true");

    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      `https://api.vimeo.com/oauth/authorize?${params.toString()}`,
      "vimeo_oauth_add",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
    );

    if (!popup) {
      setAddingAccount(false);
      setError("No se pudo abrir el popup. Revisa el bloqueador de ventanas.");
      return;
    }

    let checkPopupClosed = null;

    const cleanupFlow = () => {
      if (checkPopupClosed) clearInterval(checkPopupClosed);
      window.removeEventListener("message", handleAddMessage);
      setAddingAccount(false);
      sessionStorage.removeItem("vimeo_adding_account");
    };

    checkPopupClosed = setInterval(() => {
      if (popup && popup.closed) {
        cleanupFlow();
      }
    }, 500);

    const handleAddMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "vimeo_auth_success") {
        cleanupFlow();

        const { accessToken, userInfo } = event.data;

        try {
          setSaving(true);
          const response = await videoService.addVimeoAccount(accessToken, userInfo);

          if (response.success) {
            setSuccess(`Cuenta "${response.data?.name || 'adicional'}" agregada correctamente`);
            await loadAccounts();
          }
        } catch (err) {
          setError(err.message || "Error agregando cuenta");
        } finally {
          setSaving(false);
        }

        if (popup && !popup.closed) popup.close();
      } else if (event.data.type === "vimeo_auth_error") {
        cleanupFlow();
        setError(event.data.error || "Error en autenticación");
        if (popup && !popup.closed) popup.close();
      }
    };

    window.addEventListener("message", handleAddMessage);
  };

  const handleRemoveAccount = async (accountId) => {
    const account = accounts.find(a => a.id === accountId);
    if (!window.confirm(`¿Eliminar la cuenta "${account?.name}"?`)) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const response = await videoService.removeVimeoAccount(accountId);

      if (response.success) {
        setSuccess("Cuenta eliminada correctamente");
        await loadAccounts();
      }
    } catch (err) {
      setError(err.message || "Error eliminando cuenta");
    } finally {
      setSaving(false);
    }
  };

  const handleVimeoLogin = () => {
    // Evitar múltiples intentos simultáneos (Vimeo rate-limitea rápido)
    if (vimeoAuthInProgress) return;

    setError("");
    setSuccess("");
    setVimeoAuthInProgress(true);

    const state = Math.random().toString(36).substring(7);
    const params = new URLSearchParams({
      response_type: "code",
      client_id: VIMEO_CLIENT_ID,
      redirect_uri: VIMEO_REDIRECT_URI,
      scope: VIMEO_SCOPES,
      state: state,
    });

    // Guardar información en sessionStorage
    sessionStorage.setItem("vimeo_oauth_state", state);
    sessionStorage.setItem("vimeo_redirect_uri", VIMEO_REDIRECT_URI);

    // Abrir popup
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      `https://api.vimeo.com/oauth/authorize?${params.toString()}`,
      "vimeo_oauth",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
    );

    console.log("[Settings] Popup abierto:", popup);
    console.log("[Settings] Esperando autorización de Vimeo...");

    if (!popup) {
      setVimeoAuthInProgress(false);
      setError("No se pudo abrir el popup. Revisa el bloqueador de ventanas emergentes.");
      return;
    }

    let checkPopupClosed = null;

    const cleanupAuthFlow = () => {
      if (checkPopupClosed) clearInterval(checkPopupClosed);
      window.removeEventListener("message", handleMessage);
      setVimeoAuthInProgress(false);
    };

    // Monitor de cierre del popup
    checkPopupClosed = setInterval(() => {
      if (popup && popup.closed) {
        console.log("[Settings] Popup cerrado");
        cleanupAuthFlow();
      }
    }, 500);

    // Escuchar mensajes del popup
    const handleMessage = async (event) => {
      console.log("[Settings] Mensaje recibido:", event.data);
      console.log("[Settings] Origen:", event.origin);
      console.log("[Settings] Esperado:", window.location.origin);
      
      if (event.origin !== window.location.origin) {
        console.warn("[Settings] Origen no coincide, ignorando mensaje");
        return;
      }

      if (event.data.type === "vimeo_auth_success") {
        console.log("[Settings] ✓ Autenticación exitosa!");
        cleanupAuthFlow();

        const { accessToken, userInfo } = event.data;

        try {
          setSaving(true);
          const response = await videoService.updateVimeoConfig({
            accessToken: accessToken,
            userInfo: userInfo,
          });

          if (response.success) {
            setConfig(response.data);
            setSuccess("Vimeo autenticado correctamente");
            await loadConfig(); // Recargar para obtener userInfo completo
          }
        } catch (err) {
          setError(err.message || "Error guardando autenticación");
        } finally {
          setSaving(false);
        }

        if (popup && !popup.closed) {
          popup.close();
        }
      } else if (event.data.type === "vimeo_auth_error") {
        console.error("[Settings] ✗ Error en autenticación:", event.data.error);
        cleanupAuthFlow();
        setError(event.data.error || "Error en autenticación de Vimeo");

        if (popup && !popup.closed) {
          popup.close();
        }
      }
    };

    window.addEventListener("message", handleMessage);
  };

  const handleDisconnectVimeo = async () => {
    if (!window.confirm("¿Estás seguro de desconectar Vimeo?")) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await videoService.clearVimeoAuth();
      setSuccess("Vimeo desconectado correctamente");
      await loadConfig();
    } catch (err) {
      setError(err.message || "Error desconectando Vimeo");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Configuración
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <VideoLibrary sx={{ mr: 1, color: "primary.main", fontSize: 32 }} />
            <Typography variant="h6">Integración con Vimeo</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Toggle principal */}
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled}
                onChange={handleToggleEnabled}
                disabled={saving}
              />
            }
            label="Activar subida a Vimeo"
          />

          {config.enabled && (
            <Box sx={{ mt: 3, pl: 2 }}>
              {/* Autenticación */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  Autenticación
                </Typography>

                {config.hasToken && config.userInfo ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
                    {config.userInfo.picture && (
                      <Avatar src={config.userInfo.picture} />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" fontWeight="medium">
                        {config.userInfo.name}
                      </Typography>
                      <Chip
                        label="Conectado"
                        color="success"
                        size="small"
                        icon={<Check />}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleDisconnectVimeo}
                      disabled={saving}
                    >
                      Desconectar
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    {vimeoAuthInProgress && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Esperando autorización de Vimeo… Si Vimeo muestra “demasiadas solicitudes”, espera unos minutos e inténtalo solo una vez.
                      </Alert>
                    )}
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Necesitas conectar tu cuenta de Vimeo para subir videos
                    </Alert>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleVimeoLogin}
                      disabled={saving || vimeoAuthInProgress}
                    >
                      {vimeoAuthInProgress ? "Conectando…" : "Conectar con Vimeo"}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Opciones solo si está autenticado */}
              {config.hasToken && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {/* Modo de subida */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Modo de subida
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.autoUpload}
                          onChange={handleToggleAutoUpload}
                          disabled={saving}
                        />
                      }
                      label={
                        config.autoUpload
                          ? "Automático (después de Google Drive)"
                          : "Manual (con botón en lista de videos)"
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {config.autoUpload
                        ? "Los videos se subirán automáticamente a Vimeo después de subirse a Google Drive"
                        : "Podrás elegir manualmente qué videos subir a Vimeo desde la lista de videos"}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Configuración de privacidad */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Privacidad por defecto
                    </Typography>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <InputLabel>Privacidad</InputLabel>
                      <Select
                        value={config.privacy || "unlisted"}
                        onChange={handlePrivacyChange}
                        label="Privacidad"
                        disabled={saving}
                      >
                        <MenuItem value="unlisted">
                          Unlisted (No listado)
                        </MenuItem>
                        <MenuItem value="nobody">
                          Private (Privado)
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Las cuentas gratuitas de Vimeo solo permiten videos "Unlisted" o "Private"
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Cuentas de Vimeo conectadas */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Cuentas de Vimeo conectadas
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Selecciona a qué cuenta subir los videos. Puedes conectar múltiples cuentas.
                    </Typography>
                    
                    {loadingAccounts ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Cargando cuentas...
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {accounts.map((account) => (
                          <Box
                            key={account.id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              p: 1.5,
                              borderRadius: 1,
                              border: account.isSelected ? "2px solid" : "1px solid",
                              borderColor: account.isSelected ? "primary.main" : "divider",
                              bgcolor: account.isSelected ? "primary.50" : "transparent",
                              cursor: "pointer",
                              "&:hover": { bgcolor: "action.hover" }
                            }}
                            onClick={() => !saving && handleAccountSelect(account.id)}
                          >
                            {account.isSelected ? (
                              <RadioButtonChecked color="primary" sx={{ mr: 1.5 }} />
                            ) : (
                              <RadioButtonUnchecked color="action" sx={{ mr: 1.5 }} />
                            )}
                            
                            {account.picture ? (
                              <Avatar src={account.picture} sx={{ width: 36, height: 36, mr: 1.5 }} />
                            ) : (
                              <Avatar sx={{ width: 36, height: 36, mr: 1.5 }}>
                                {account.name?.charAt(0) || "V"}
                              </Avatar>
                            )}
                            
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={account.isSelected ? "bold" : "normal"}>
                                {account.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {account.isPrimary ? "Cuenta principal" : "Cuenta adicional"}
                                {account.accountType && ` • ${account.accountType}`}
                              </Typography>
                            </Box>
                            
                            {account.isSelected && (
                              <Chip label="Activa" color="primary" size="small" sx={{ mr: 1 }} />
                            )}
                            
                            {!account.isPrimary && (
                              <Button
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveAccount(account.id);
                                }}
                                disabled={saving}
                                sx={{ minWidth: "auto", p: 0.5 }}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            )}
                          </Box>
                        ))}
                        
                        {/* Botón para agregar cuenta */}
                        <Button
                          variant="outlined"
                          startIcon={addingAccount ? <CircularProgress size={16} /> : <Add />}
                          onClick={handleAddAccount}
                          disabled={saving || addingAccount}
                          sx={{ mt: 1 }}
                        >
                          {addingAccount ? "Conectando..." : "Conectar otra cuenta de Vimeo"}
                        </Button>
                        
                        <Alert severity="info" sx={{ mt: 1 }}>
                          Para subir a otra cuenta de Vimeo, inicia sesión con esa cuenta al hacer clic en "Conectar otra cuenta".
                        </Alert>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <VpnKey sx={{ mr: 1, color: "primary.main", fontSize: 32 }} />
            <Typography variant="h6">API de Consulta</Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Esta API es solo de lectura y permite consultar videos disponibles.
          </Typography>

          <Stack spacing={1} sx={{ mb: 2 }}>
            <Typography variant="body2">
              Estado de API Key:{" "}
              <strong>{apiKeyStatus.hasKey ? "Configurada" : "No configurada"}</strong>
            </Typography>
            {apiKeyStatus.maskedKey && (
              <Typography variant="body2">
                API Key actual (enmascarada): <strong>{apiKeyStatus.maskedKey}</strong>
              </Typography>
            )}
            {apiKeyStatus.lastGeneratedAt && (
              <Typography variant="caption" color="text.secondary">
                Última generación:{" "}
                {new Date(apiKeyStatus.lastGeneratedAt).toLocaleString("es-CL")}
              </Typography>
            )}
          </Stack>

          {apiKeyVisible && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Copia y guarda esta API Key, no volverá a mostrarse completa:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "background.default",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  fontSize: "0.85rem",
                  mb: 1,
                }}
              >
                {apiKeyVisible}
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopy />}
                onClick={handleCopyApiKey}
              >
                Copiar API Key
              </Button>
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={apiKeyStatus.hasKey ? <Autorenew /> : <VpnKey />}
            onClick={handleGenerateApiKey}
            disabled={apiKeyLoading}
          >
            {apiKeyLoading
              ? "Generando..."
              : apiKeyStatus.hasKey
                ? "Regenerar API Key"
                : "Generar API Key"}
          </Button>
        </CardContent>
      </Card>

      {/* Card de configuración general */}
      {(currentUser?.role === 'admin' || currentUser?.permissions?.settings) && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <SettingsIcon sx={{ mr: 1, color: "primary.main", fontSize: 32 }} />
              <Typography variant="h6">Configuración General</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Tamaño máximo de archivo */}
            <Box>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Tamaño máximo de archivo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define el tamaño máximo permitido para subir archivos de video. (Valor actual: <strong>{appSettings.maxFileSizeGB} GB</strong>)
              </Typography>

              <Box sx={{ px: 1, mb: 2 }}>
                <Slider
                  value={pendingMaxFileSize ?? appSettings.maxFileSizeGB}
                  onChange={(e, newValue) => setPendingMaxFileSize(newValue)}
                  min={0.5}
                  max={20}
                  step={0.5}
                  marks={[
                    { value: 0.5, label: '0.5 GB' },
                    { value: 2.5, label: '2.5 GB' },
                    { value: 5, label: '5 GB' },
                    { value: 10, label: '10 GB' },
                    { value: 15, label: '15 GB' },
                    { value: 20, label: '20 GB' },
                  ]}
                  valueLabelDisplay="on"
                  valueLabelFormat={(value) => `${value} GB`}
                  disabled={appSettingsLoading}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <TextField
                  label="Tamaño (GB)"
                  type="number"
                  value={pendingMaxFileSize ?? appSettings.maxFileSizeGB}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val) && val >= 0.1 && val <= 50) {
                      setPendingMaxFileSize(val);
                    }
                  }}
                  inputProps={{ min: 0.1, max: 50, step: 0.1 }}
                  size="small"
                  sx={{ width: 140 }}
                  disabled={appSettingsLoading}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveMaxFileSize}
                  disabled={appSettingsLoading || pendingMaxFileSize === appSettings.maxFileSizeGB}
                  startIcon={appSettingsLoading ? <CircularProgress size={16} /> : null}
                >
                  {appSettingsLoading ? 'Guardando...' : 'Guardar'}
                </Button>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                Este límite aplica tanto a la validación en el navegador como en el servidor. Si lo cambias, se aplicará inmediatamente para nuevas subidas.
              </Alert>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Card de gestión de usuarios — solo admin */}
      {(currentUser?.role === 'admin' || currentUser?.permissions?.manage_users) && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PeopleIcon sx={{ mr: 1, color: "primary.main", fontSize: 32 }} />
              <Typography variant="h6">Gestión de Usuarios</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <UserManagement currentUser={currentUser} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Settings;


